import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/auth';
import { supabase } from '@/integrations/supabase/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { QuestState, QuestStep, QuestContextType } from '@/types/quest-types';
import { QUEST_STEPS, getQuestStep, getNextQuestStep, isQuestCompleted, TOTAL_QUEST_STEPS } from '@/config/quest-steps';

const INITIAL_QUEST_STATE: QuestState = {
  currentStep: 1,
  isActive: false,
  isCompleted: false,
  completedSteps: [],
  lastActionWasSkip: false,
};

export const useQuestSystem = (): QuestContextType => {
  const [questState, setQuestState] = useState<QuestState>(INITIAL_QUEST_STATE);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Load quest state from database on mount
  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const loadQuestState = async () => {
      try {
        const { data: userData, error } = await supabase
          .from('users')
          .select('onboarding_quest')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('[useQuestSystem] Error loading quest state:', error);
          setIsLoading(false);
          return;
        }

        const questProgress = userData?.onboarding_quest ?? null;
        
        // CRITICAL: If quest is fully completed (step 9), don't run ANY validation or modifications
        if (questProgress === 9) {
          console.log('[useQuestSystem] Quest is fully completed (step 9) - skipping all validation');
          setQuestState({
            currentStep: TOTAL_QUEST_STEPS + 1,
            isActive: false,
            isCompleted: true,
            completedSteps: Array.from({ length: TOTAL_QUEST_STEPS }, (_, i) => i + 1),
            lastActionWasSkip: false,
          });
          setIsLoading(false);
          return; // EXIT EARLY - no validation needed for completed quests
        }
        
        // Convert database progress to quest state
        let questState: QuestState;
        
        if (questProgress === null) {
          // User has never started quest - not active yet
          questState = {
            currentStep: 1,
            isActive: false,
            isCompleted: false,
            completedSteps: [],
            lastActionWasSkip: false,
          };
        } else if (questProgress >= TOTAL_QUEST_STEPS) {
          // Quest completed - show celebration step
          questState = {
            currentStep: TOTAL_QUEST_STEPS + 1,
            isActive: true,
            isCompleted: false,
            completedSteps: Array.from({ length: TOTAL_QUEST_STEPS }, (_, i) => i + 1),
            lastActionWasSkip: false,
          };
        } else {
          // Quest in progress (questProgress = 0 means started but no steps completed yet)
          const completedSteps = questProgress > 0 ? Array.from({ length: questProgress }, (_, i) => i + 1) : [];
          questState = {
            currentStep: questProgress + 1,
            isActive: true,
            isCompleted: false,
            completedSteps,
            lastActionWasSkip: false,
          };
        }

        console.log('[useQuestSystem] Loaded quest state:', { questProgress, questState });
        setQuestState(questState);
        
        // REMOVED: Project validation that was resetting quest progress
        // Quest should ONLY be modified by complete/skip actions, never by validation

      } catch (error) {
        console.error('[useQuestSystem] Error loading quest state:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadQuestState();
  }, [user]);

  // Get current quest step
  const currentQuestStep = questState.isActive && !questState.isCompleted 
    ? getQuestStep(questState.currentStep) 
    : null;

  // Start the quest system - ONLY changes local state to show quest UI
  // NEVER modifies database - quest progress is only changed by complete/skip
  const startQuest = useCallback(async () => {
    if (!user) return;

    // Simply activate the quest UI without touching the database
    // The actual step will be determined by the database value
    setQuestState(prevState => ({
      ...prevState,
      isActive: true
    }));

    console.log('[useQuestSystem] Quest UI activated (database unchanged)');
  }, [user]);

  // Complete a quest step
  const completeQuest = useCallback(async (stepId: number) => {
    if (!user || !questState.isActive) return;

    try {
      // Special handling for celebration step (step 9)
      if (stepId === 9) {
        // Mark quest as fully completed (use 9 to indicate celebration acknowledged)
        const { error } = await supabase
          .from('users')
          .update({ onboarding_quest: 9 })
          .eq('id', user.id);

        if (error) {
          console.error('[useQuestSystem] Error completing quest celebration:', error);
          return;
        }

        // Update local state to fully completed
        setQuestState({
          currentStep: 9,
          isActive: false,
          isCompleted: true,
          completedSteps: Array.from({ length: TOTAL_QUEST_STEPS }, (_, i) => i + 1),
          lastActionWasSkip: false,
        });

        console.log('[useQuestSystem] Quest celebration completed - quest fully finished!');
        return;
      }

      // Update database with new progress
      const { error } = await supabase
        .from('users')
        .update({ onboarding_quest: stepId })
        .eq('id', user.id);

      if (error) {
        console.error('[useQuestSystem] Error completing quest step:', error);
        return;
      }

      // Update local state
      const newCompletedSteps = [...questState.completedSteps];
      if (!newCompletedSteps.includes(stepId)) {
        newCompletedSteps.push(stepId);
      }

      // Check if this completes all quest steps (triggers celebration)
      if (stepId === TOTAL_QUEST_STEPS) {
        // Show celebration step
        setQuestState({
          currentStep: TOTAL_QUEST_STEPS + 1, // Step 9 (celebration)
          isActive: true,
          isCompleted: false,
          completedSteps: newCompletedSteps,
          lastActionWasSkip: false,
        });
        console.log(`[useQuestSystem] All quest steps completed! Showing celebration step.`);
      } else {
        // Continue to next step
        setQuestState({
          currentStep: stepId + 1,
          isActive: true,
          isCompleted: false,
          completedSteps: newCompletedSteps,
          lastActionWasSkip: false,
        });
        console.log(`[useQuestSystem] Quest step ${stepId} completed successfully`);
      }
    } catch (error) {
      console.error('[useQuestSystem] Error completing quest step:', error);
    }
  }, [user, questState]);

  // Skip the current quest step
  const skipCurrentStep = useCallback(async () => {
    if (!user || !questState.isActive) return;

    try {
      const currentStepNumber = questState.currentStep;
      
      // If we're at step 8, set to 9 to fully complete and avoid celebration
      if (currentStepNumber >= TOTAL_QUEST_STEPS) {
        const { error } = await supabase
          .from('users')
          .update({ onboarding_quest: 9 })
          .eq('id', user.id);

        if (error) {
          console.error('[useQuestSystem] Error completing quest:', error);
          return;
        }

        setQuestState({
          currentStep: TOTAL_QUEST_STEPS + 1,
          isActive: false,
          isCompleted: true,
          completedSteps: Array.from({ length: TOTAL_QUEST_STEPS }, (_, i) => i + 1),
          lastActionWasSkip: true,
        });

        console.log('[useQuestSystem] Quest completed - no celebration shown');
      } else {
        // Normal skip to next step (for steps 1-7)
        const completedStepNumber = currentStepNumber;
        
        const { error } = await supabase
          .from('users')
          .update({ onboarding_quest: completedStepNumber })
          .eq('id', user.id);

        if (error) {
          console.error('[useQuestSystem] Error skipping step:', error);
          return;
        }

        const newCompletedSteps = Array.from({ length: completedStepNumber }, (_, i) => i + 1);
        
        setQuestState({
          currentStep: completedStepNumber + 1,
          isActive: true,
          isCompleted: false,
          completedSteps: newCompletedSteps,
          lastActionWasSkip: true,
        });

        console.log(`[useQuestSystem] Skipped to step ${completedStepNumber + 1}`);
      }
    } catch (error) {
      console.error('[useQuestSystem] Error skipping step:', error);
    }
  }, [user, questState]);

  // Skip the entire quest system (for backwards compatibility)
  const skipQuest = useCallback(async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('users')
        .update({ onboarding_quest: 9 }) // Set to 9 to fully complete and hide quest
        .eq('id', user.id);

      if (error) {
        console.error('[useQuestSystem] Error skipping quest:', error);
        return;
      }

      setQuestState({
        currentStep: TOTAL_QUEST_STEPS + 1,
        isActive: false,
        isCompleted: true,
        completedSteps: Array.from({ length: TOTAL_QUEST_STEPS }, (_, i) => i + 1),
        lastActionWasSkip: true,
      });

      console.log('[useQuestSystem] Quest skipped successfully - quest fully hidden');
    } catch (error) {
      console.error('[useQuestSystem] Error skipping quest:', error);
    }
  }, [user]);

  // Check if a specific step is completed
  const isStepCompleted = useCallback((stepId: number): boolean => {
    return questState.completedSteps.includes(stepId);
  }, [questState.completedSteps]);

  // Get the next incomplete step
  const getNextIncompleteStep = useCallback((): QuestStep | null => {
    for (let i = 1; i <= TOTAL_QUEST_STEPS; i++) {
      if (!isStepCompleted(i)) {
        return getQuestStep(i);
      }
    }
    return null;
  }, [isStepCompleted]);

  // Get user's first project ID for dynamic routes
  const getUserFirstProjectId = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    
    try {
      // Get the workbook_id (actual project ID) instead of goal_id
      const { data: projects, error } = await supabase
        .from('planner_goals_workbooks')
        .select('workbook_id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true })
        .limit(1);

      if (error || !projects || projects.length === 0) {
        console.log('[useQuestSystem] No projects found for user');
        return null;
      }

      const projectId = projects[0].workbook_id;
      console.log('[useQuestSystem] Found project ID (workbook_id):', projectId);
      
      // Test if this project actually exists and can be loaded
      const { data: projectTest, error: projectError } = await supabase.rpc('get_project_navigation', {
        p_project_id: projectId
      });
      
      if (projectError || !projectTest || projectTest.length === 0) {
        console.error('[useQuestSystem] Project ID exists but cannot load project data:', projectError, projectTest);
        return null;
      }
      
      console.log('[useQuestSystem] Project data validated successfully');
      return projectId;
    } catch (error) {
      console.error('[useQuestSystem] Error fetching user projects:', error);
      return null;
    }
  }, [user]);

  // Check if user is on the correct page for current quest step
  const isOnCorrectPage = useCallback((step: QuestStep): boolean => {
    const currentPath = location.pathname;
    
    // Handle dynamic routes (e.g., /project/:id/tasks)
    if (step.page.includes(':id')) {
      const pattern = step.page.replace(':id', '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      return regex.test(currentPath);
    }
    
    return currentPath === step.page;
  }, [location.pathname]);

  // Auto-navigate to quest step if needed (optional behavior)
  const navigateToQuestStep = useCallback((step: QuestStep) => {
    if (!isOnCorrectPage(step)) {
      // For dynamic routes, we'll need to handle navigation differently
      // This would need project context for routes like /project/:id/tasks
      if (!step.page.includes(':id')) {
        navigate(step.page);
      }
    }
  }, [navigate, isOnCorrectPage]);

  // Get current quest step with resolved project URLs
  const getCurrentQuestStepWithResolvedUrl = useCallback(async (): Promise<QuestStep | null> => {
    if (!currentQuestStep) return null;

    // For steps that need project URLs, resolve the project ID
    if (currentQuestStep.page.includes(':id')) {
      const projectId = await getUserFirstProjectId();
      if (!projectId) {
        // If no project exists, redirect them to create one first
        console.log('[useQuestSystem] No project found, user needs to create project first');
        return getQuestStep(4); // CREATE_PROJECT step
      }

      console.log('[useQuestSystem] Resolving project URL with ID:', projectId);
      // Return step with resolved URL
      return {
        ...currentQuestStep,
        page: currentQuestStep.page.replace(':id', projectId)
      };
    }

    return currentQuestStep;
  }, [currentQuestStep, getUserFirstProjectId]);

  // Check if current quest step requires a project and if project exists
  const canProgressToProjectSteps = useCallback(async (): Promise<boolean> => {
    if (!currentQuestStep) return false;
    
    // Steps 5-8 require a project
    if (currentQuestStep.id >= 5) {
      const projectId = await getUserFirstProjectId();
      return projectId !== null;
    }
    
    return true;
  }, [currentQuestStep, getUserFirstProjectId]);

  return {
    questState,
    currentQuestStep,
    completeQuest,
    startQuest,
    skipQuest,
    skipCurrentStep,
    isStepCompleted,
    getNextIncompleteStep,
    isOnCorrectPage,
    getCurrentQuestStepWithResolvedUrl,
    canProgressToProjectSteps,
    getUserFirstProjectId,
    isLoading,
  };
};